import { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import MojitoServices from 'mojito/services';
import MojitoPresentation from 'mojito/presentation';
import EventsCoupon from 'modules/events-coupon/index.js';
import FavoritesContentOnboarding from 'modules/inplay/favorites-content-onboarding/index.js';
import InPlayTypes from 'modules/inplay/types.js';
import InPlayUtils from 'modules/inplay/utils.js';
import { EventListContextProvider } from 'modules/event-list/context/event-list-context.js';

const { ExpandableView, TextView, FlexPane, Text } = MojitoPresentation.Components;
const { ALL_SPORTS_CODE, STREAMING, FAVORITES } = InPlayTypes.TABS;
const {
    slice: { actions: favoritesActions },
} = MojitoServices.Favorites;

function InPlayContent(props) {
    const {
        mojitoTools: { appContext, config, emitPerformanceMark, style, stringResolver },
        selectedTab,
        sportIds,
        eventGroups,
        favoriteEvents,
    } = props;
    const { sportName, subscriptionResolver } = appContext;
    const dispatch = useDispatch();
    const loadedEventGroupIdsRef = useRef({});
    const [dataLoaded, setDataLoaded] = useState(false);

    useEffect(() => {
        dispatch(favoritesActions.fetchFavoriteEventIds());
    }, [dispatch]);

    useEffect(() => {
        loadedEventGroupIdsRef.current = {};
    }, [selectedTab]);

    useEffect(() => {
        emitPerformanceMark?.('moduleRendered', dataLoaded);
    }, [emitPerformanceMark, dataLoaded]);

    const isDataLoaded = useCallback(() => {
        const allEventGroupIds = sportIds.map(id =>
            subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(id)
        );
        const loadedEventGroupIds = Object.keys(loadedEventGroupIdsRef.current);

        if (selectedTab === ALL_SPORTS_CODE) {
            return allEventGroupIds.every(id => loadedEventGroupIds.includes(id));
        }

        return (
            loadedEventGroupIds[0] ===
            subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(selectedTab)
        );
    }, [selectedTab, sportIds, subscriptionResolver]);

    const onDataLoad = useCallback(
        eventGroupId => {
            loadedEventGroupIdsRef.current[eventGroupId] = eventGroupId;
            if (isDataLoaded() && !dataLoaded) {
                setDataLoaded(true);
            }
        },
        [dataLoaded, isDataLoaded]
    );

    function renderEventsCoupon(sportTab) {
        return (
            <EventsCoupon
                sportId={sportTab}
                eventGroupsId={subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(sportTab)}
                config={style.sportTabEventCoupon}
                isInPlay={true}
                analyticsName={`InplayCoupon (${sportTab})`}
                onDataLoad={onDataLoad}
            />
        );
    }

    function renderEventLists(sportIds, tabId) {
        return sportIds.map(sportCode => {
            const numberOfEvents = InPlayUtils.countEventsForInplayMenuTab(
                sportCode,
                eventGroups,
                favoriteEvents
            );

            return (
                <EventListContextProvider key={sportCode} numberOfEvents={numberOfEvents}>
                    <Text config={config.sportTitle}>{sportName(sportCode)}</Text>
                    <EventsCoupon
                        sportId={sportCode}
                        eventGroupsId={subscriptionResolver.liveAndUpcomingMatchAndOutrightEvents(
                            tabId === STREAMING ? tabId : sportCode
                        )}
                        key={sportCode}
                        config={style[`${tabId}TabEventCoupon`]}
                        isInPlay={true}
                        isInPlayStreaming={tabId === STREAMING}
                        isFavorites={tabId === FAVORITES}
                        analyticsName={`InplayCoupon (${sportCode})`}
                        onDataLoad={onDataLoad}
                        shouldRequestData={false}
                    />
                </EventListContextProvider>
            );
        });
    }

    function renderContent() {
        if (selectedTab === ALL_SPORTS_CODE && sportIds) {
            return renderEventLists(InPlayUtils.omitStreamingId(sportIds), ALL_SPORTS_CODE);
        }

        if (selectedTab === STREAMING) {
            const streamingEventGroups = eventGroups[STREAMING];
            const streamingSportIds = new Set(streamingEventGroups?.map(group => group.sportId));
            return renderEventLists([...streamingSportIds], STREAMING);
        }

        if (selectedTab === FAVORITES) {
            const favoriteSportIds = InPlayUtils.getFavoritizedSportIds(
                eventGroups,
                favoriteEvents
            );
            return renderEventLists(favoriteSportIds, FAVORITES);
        }

        return renderEventsCoupon(selectedTab);
    }

    function renderOnboarding() {
        if (selectedTab === FAVORITES) {
            return favoriteEvents.length < 1 ? (
                <FavoritesContentOnboarding
                    config={config.favoritesContentOnboarding}
                    closable={false}
                />
            ) : null;
        }

        return <FavoritesContentOnboarding config={config.favoritesContentOnboarding} />;
    }

    return (
        <FlexPane config={config.container}>
            {renderOnboarding()}
            {renderContent()}
            {config.showDisclaimer && (
                <ExpandableView
                    class="ta-inPlayDisclaimer"
                    config={config.disclaimer}
                    headerText={stringResolver.resolveString('$INPLAY.INPLAY_DISCLAIMER.HEADER')}
                    expanded={config.disclaimerInitiallyExpanded}
                >
                    <TextView
                        config={config.disclaimerText}
                        text={stringResolver.resolveString('$INPLAY.INPLAY_DISCLAIMER.TEXT')}
                    />
                </ExpandableView>
            )}
        </FlexPane>
    );
}

InPlayContent.getStyle = (config, applicationMode, merge) => {
    const {
        eventsCouponPerTab: { base, all, sport, streaming, favorites },
    } = config;

    return {
        allTabEventCoupon: merge(base, all),
        sportTabEventCoupon: merge(base, sport),
        streamingTabEventCoupon: merge(base, streaming),
        favoritesTabEventCoupon: merge(base, favorites),
    };
};

export default InPlayContent;
